/**
 * EffectsSystem.js
 *
 * Phase 12a — Visual Upgrades.
 *
 * Centralized particle / FX manager for the GameScene. Responsibilities:
 *   - Build a procedural "soft circle" texture once and reuse it for every
 *     ParticleSystem so we never need a static asset file.
 *   - Provide bursty one-shot effects (weapon impact, mining sparks,
 *     structure build "poof").
 *   - Provide a continuous ship-exhaust emitter that scales with throttle.
 *   - Provide a long-lived biome-haze emitter parented to the player so
 *     atmospheric props (lava embers, ice flurries, toxic motes) feel native.
 *
 * The system is designed to fail safe: every public method is a no-op if the
 * particle texture didn't initialize, so a misconfigured environment never
 * breaks the rest of the scene.
 */

import {
  ParticleSystem,
  Color4,
  Color3,
  Vector3,
  DynamicTexture,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
} from '@babylonjs/core';

const HEX = /^#?([0-9a-f]{6})$/i;
function hexToColor4(hex, alpha = 1) {
  const m = HEX.exec(hex || '');
  if (!m) return new Color4(1, 1, 1, alpha);
  const v = m[1];
  return new Color4(
    parseInt(v.substring(0, 2), 16) / 255,
    parseInt(v.substring(2, 4), 16) / 255,
    parseInt(v.substring(4, 6), 16) / 255,
    alpha,
  );
}

export default class EffectsSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this._disposed = false;
    this._systems = [];           // one-shots tracked for disposal-after-burn
    this._continuous = [];        // long-lived emitters

    this._sparkTex = this._buildSparkTexture();
    this._exhaust = null;         // { ps, anchors: [TransformNode] }
    this._biomeHaze = null;
    this._shockwaves = [];        // expanding explosion rings (decayed in tick)
    this._flashes = [];           // brief explosion point-lights (decayed in tick)
    this._lastNow = performance.now();
  }

  _buildSparkTexture() {
    try {
      const size = 64;
      const dt = new DynamicTexture('effectsSpark', { width: size, height: size }, this.scene, false);
      const ctx = dt.getContext();
      const grad = ctx.createRadialGradient(size / 2, size / 2, 1, size / 2, size / 2, size / 2);
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.35, 'rgba(255,255,255,0.65)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
      dt.hasAlpha = true;
      dt.update();
      return dt;
    } catch (_e) {
      return null;
    }
  }

  // -------- One-shot bursts --------

  /** Short, bright spark burst at a world position (e.g. projectile impact). */
  spawnImpact(pos, color = '#7cf2ff', count = 24) {
    if (this._disposed || !this._sparkTex) return;
    const ps = new ParticleSystem(`fxImpact_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, count, this.scene);
    ps.particleTexture = this._sparkTex;
    ps.emitter = pos.clone();
    ps.minEmitBox = new Vector3(0, 0, 0);
    ps.maxEmitBox = new Vector3(0, 0, 0);
    const base = hexToColor4(color, 1);
    ps.color1 = base;
    ps.color2 = new Color4(1, 1, 1, 1);
    ps.colorDead = new Color4(base.r, base.g, base.b, 0);
    ps.minSize = 0.08;
    ps.maxSize = 0.22;
    ps.minLifeTime = 0.18;
    ps.maxLifeTime = 0.45;
    ps.emitRate = 0;        // burst only
    ps.manualEmitCount = count;
    ps.gravity = new Vector3(0, -4, 0);
    ps.direction1 = new Vector3(-1, 1, -1);
    ps.direction2 = new Vector3(1, 2.5, 1);
    ps.minEmitPower = 1.5;
    ps.maxEmitPower = 4.5;
    ps.updateSpeed = 0.012;
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.disposeOnStop = true;
    ps.start();
    ps.stop(); // burst is already emitted via manualEmitCount; stop schedules disposal once particles die
    this._systems.push(ps);
  }

  /** Tiny continuous-ish spark fountain at the drilled target (called every frame
   * while mining). To avoid creating a system per frame, we keep a single shared
   * mining emitter parented to a moving anchor; callers just keep moving the anchor. */
  spawnMiningSparks(pos, color = '#ffb070') {
    if (this._disposed || !this._sparkTex) return;
    if (!this._mining) {
      const ps = new ParticleSystem('fxMining', 80, this.scene);
      ps.particleTexture = this._sparkTex;
      ps.emitter = new Vector3(0, 0, 0);
      ps.minEmitBox = new Vector3(-0.1, 0, -0.1);
      ps.maxEmitBox = new Vector3(0.1, 0.05, 0.1);
      ps.color1 = hexToColor4(color, 1);
      ps.color2 = new Color4(1, 0.85, 0.55, 1);
      ps.colorDead = new Color4(0.4, 0.2, 0.1, 0);
      ps.minSize = 0.05;
      ps.maxSize = 0.15;
      ps.minLifeTime = 0.15;
      ps.maxLifeTime = 0.35;
      ps.emitRate = 80;
      ps.gravity = new Vector3(0, -6, 0);
      ps.direction1 = new Vector3(-0.6, 1.2, -0.6);
      ps.direction2 = new Vector3(0.6, 2.4, 0.6);
      ps.minEmitPower = 0.8;
      ps.maxEmitPower = 2.5;
      ps.updateSpeed = 0.012;
      ps.blendMode = ParticleSystem.BLENDMODE_ADD;
      ps.start();
      this._mining = ps;
      this._continuous.push(ps);
    }
    this._mining.emitter = pos.clone();
    this._mining._lastTickAt = performance.now();
  }

  /** Soft greenish nanite poof when committing a build. */
  spawnBuildPoof(pos, color = '#7effc8') {
    if (this._disposed || !this._sparkTex) return;
    const ps = new ParticleSystem(`fxPoof_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, 40, this.scene);
    ps.particleTexture = this._sparkTex;
    ps.emitter = pos.clone();
    ps.minEmitBox = new Vector3(-0.4, 0, -0.4);
    ps.maxEmitBox = new Vector3(0.4, 0.1, 0.4);
    const base = hexToColor4(color, 1);
    ps.color1 = base;
    ps.color2 = new Color4(1, 1, 1, 0.8);
    ps.colorDead = new Color4(base.r, base.g, base.b, 0);
    ps.minSize = 0.18;
    ps.maxSize = 0.5;
    ps.minLifeTime = 0.5;
    ps.maxLifeTime = 1.2;
    ps.emitRate = 0;
    ps.manualEmitCount = 40;
    ps.gravity = new Vector3(0, 1.0, 0);   // float up
    ps.direction1 = new Vector3(-1.5, 1, -1.5);
    ps.direction2 = new Vector3(1.5, 2, 1.5);
    ps.minEmitPower = 0.4;
    ps.maxEmitPower = 1.4;
    ps.updateSpeed = 0.012;
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.disposeOnStop = true;
    ps.start();
    ps.stop();
    this._systems.push(ps);
  }

  /** Big explosion: ember/debris burst + an expanding shockwave ring + a brief
   *  point-light flash. Used on enemy / ship kills and big impacts. */
  spawnExplosion(pos, color = '#ff7a3a', scale = 1) {
    if (this._disposed) return;
    const base = hexToColor4(color, 1);
    // 1) Ember / debris burst
    if (this._sparkTex) {
      const count = Math.round(56 * scale);
      const ps = new ParticleSystem(`fxBoom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, count, this.scene);
      ps.particleTexture = this._sparkTex;
      ps.emitter = pos.clone();
      ps.minEmitBox = new Vector3(0, 0, 0);
      ps.maxEmitBox = new Vector3(0, 0, 0);
      ps.color1 = base;
      ps.color2 = new Color4(1, 0.82, 0.35, 1);
      ps.colorDead = new Color4(0.25, 0.08, 0.04, 0);
      ps.minSize = 0.18 * scale; ps.maxSize = 0.7 * scale;
      ps.minLifeTime = 0.3; ps.maxLifeTime = 0.95;
      ps.emitRate = 0; ps.manualEmitCount = count;
      ps.gravity = new Vector3(0, -3, 0);
      ps.direction1 = new Vector3(-1, -1, -1);
      ps.direction2 = new Vector3(1, 1.3, 1);
      ps.minEmitPower = 3 * scale; ps.maxEmitPower = 10 * scale;
      ps.updateSpeed = 0.014;
      ps.blendMode = ParticleSystem.BLENDMODE_ADD;
      ps.disposeOnStop = true;
      ps.start(); ps.stop();
      this._systems.push(ps);
    }
    // 2) Expanding shockwave ring (decayed in tick)
    try {
      const ring = MeshBuilder.CreateTorus(`fxRing_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, {
        diameter: 1, thickness: 0.16, tessellation: 30,
      }, this.scene);
      const rmat = new StandardMaterial(`fxRingMat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, this.scene);
      rmat.disableLighting = true;
      rmat.emissiveColor = new Color3(base.r, base.g, base.b);
      rmat.alpha = 0.9;
      rmat.backFaceCulling = false;
      ring.material = rmat;
      ring.position = pos.clone();
      ring.rotation.x = Math.PI / 2;
      ring.isPickable = false;
      this._shockwaves.push({ mesh: ring, mat: rmat, t: 0, dur: 0.5, maxScale: 11 * scale });
    } catch (_e) { /* noop */ }
    // 3) Brief flash light
    try {
      const light = new PointLight(`fxFlash_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, pos.clone(), this.scene);
      light.diffuse = new Color3(base.r, base.g, base.b);
      light.intensity = 7 * scale;
      light.range = 32 * scale;
      this._flashes.push({ light, t: 0, dur: 0.18, base: 7 * scale });
    } catch (_e) { /* noop */ }
  }

  /** Brief bright muzzle flash at a weapon barrel (forward spark puff). */
  spawnMuzzleFlash(pos, dir, color = '#ff5a3a') {
    if (this._disposed || !this._sparkTex) return;
    const ps = new ParticleSystem(`fxMuzzle_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, 10, this.scene);
    ps.particleTexture = this._sparkTex;
    ps.emitter = pos.clone();
    const base = hexToColor4(color, 1);
    ps.color1 = base;
    ps.color2 = new Color4(1, 1, 1, 1);
    ps.colorDead = new Color4(base.r, base.g, base.b, 0);
    ps.minSize = 0.12; ps.maxSize = 0.45;
    ps.minLifeTime = 0.04; ps.maxLifeTime = 0.12;
    ps.emitRate = 0; ps.manualEmitCount = 10;
    const d = dir || new Vector3(0, 0, 1);
    ps.direction1 = new Vector3(d.x - 0.3, d.y - 0.3, d.z - 0.3);
    ps.direction2 = new Vector3(d.x + 0.3, d.y + 0.3, d.z + 0.3);
    ps.minEmitPower = 3; ps.maxEmitPower = 7;
    ps.gravity = new Vector3(0, 0, 0);
    ps.updateSpeed = 0.01;
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.disposeOnStop = true;
    ps.start(); ps.stop();
    this._systems.push(ps);
  }

  // -------- Continuous emitters --------

  /** Attach a continuous exhaust trail to a ship root TransformNode.
   *  `anchors` is an array of local-space offsets that mark each thruster tip;
   *  we create one anchor TransformNode per offset, parented to shipRoot. */
  attachShipExhaust(shipRoot, anchors) {
    if (this._disposed || !this._sparkTex || !shipRoot) return;
    if (this._exhaust) return; // already attached

    const anchorNodes = [];
    for (const off of anchors || [new Vector3(0, 1.4, -3.85)]) {
      const n = new TransformNode('exhaustAnchor', this.scene);
      n.parent = shipRoot;
      n.position = off.clone();
      anchorNodes.push(n);
    }

    const systems = anchorNodes.map((n, i) => {
      const ps = new ParticleSystem(`fxExhaust_${i}`, 220, this.scene);
      ps.particleTexture = this._sparkTex;
      ps.emitter = n;
      ps.minEmitBox = new Vector3(-0.05, -0.05, 0);
      ps.maxEmitBox = new Vector3(0.05, 0.05, 0);
      ps.color1 = new Color4(0.55, 0.85, 1.0, 0.95);
      ps.color2 = new Color4(0.3, 0.55, 1.0, 0.7);
      ps.colorDead = new Color4(0.1, 0.2, 0.5, 0);
      ps.minSize = 0.12;
      ps.maxSize = 0.35;
      ps.minLifeTime = 0.15;
      ps.maxLifeTime = 0.6;
      ps.emitRate = 18; // idle baseline
      ps.gravity = new Vector3(0, 0, 0);
      // Local-space direction: -Z is "behind the ship"
      ps.direction1 = new Vector3(-0.1, -0.1, -1.5);
      ps.direction2 = new Vector3(0.1, 0.1, -3.2);
      ps.minEmitPower = 2.0;
      ps.maxEmitPower = 6.0;
      ps.updateSpeed = 0.012;
      ps.blendMode = ParticleSystem.BLENDMODE_ADD;
      ps.isLocal = true; // emit in local space of the anchor so the trail rotates with the ship
      ps.start();
      this._continuous.push(ps);
      return ps;
    });

    this._exhaust = { anchors: anchorNodes, systems };
    this.setShipExhaustIntensity(0, false);
  }

  /** Reposition the exhaust anchors (used after the ship model is fitted). */
  setShipExhaustAnchors(anchors) {
    const ex = this._exhaust;
    if (!ex || !anchors) return;
    for (let i = 0; i < ex.anchors.length && i < anchors.length; i++) {
      ex.anchors[i].position.copyFrom(anchors[i]);
    }
  }

  /** Scale exhaust by throttle (0..1) and boost flag. */
  setShipExhaustIntensity(throttle, boost) {
    const ex = this._exhaust;
    if (!ex) return;
    const t = Math.max(0, Math.min(1, Number(throttle) || 0));
    const k = boost ? 1.5 : 1.0;
    for (const ps of ex.systems) {
      ps.emitRate = (18 + 220 * t) * k;
      if (boost) {
        ps.color1 = new Color4(1.0, 0.85, 0.45, 1.0);
        ps.color2 = new Color4(1.0, 0.55, 0.25, 0.85);
      } else {
        ps.color1 = new Color4(0.55, 0.85, 1.0, 0.95);
        ps.color2 = new Color4(0.3, 0.55, 1.0, 0.7);
      }
    }
  }

  /** Hide exhaust entirely (e.g. on dispose / when ship is hidden in space). */
  setShipExhaustEnabled(enabled) {
    const ex = this._exhaust;
    if (!ex) return;
    for (const ps of ex.systems) {
      if (enabled) ps.start(); else ps.stop();
    }
  }

  // -------- Biome haze (atmospheric ambient) --------

  /** Attach a long-lived emitter that follows the player, themed per biome.
   *  Pass null/undefined to clear. */
  setBiomeHaze(kind) {
    if (this._disposed) return;
    // Tear down existing haze first
    if (this._biomeHaze) {
      try { this._biomeHaze.ps.dispose(); } catch (_e) { /* noop */ }
      try { this._biomeHaze.anchor.dispose(); } catch (_e) { /* noop */ }
      this._biomeHaze = null;
    }
    if (!kind || !this._sparkTex) return;

    const anchor = new TransformNode('biomeHazeAnchor', this.scene);
    const ps = new ParticleSystem(`fxBiomeHaze_${kind}`, 220, this.scene);
    ps.particleTexture = this._sparkTex;
    ps.emitter = anchor;
    // Emit around the player in a wide box
    ps.minEmitBox = new Vector3(-60, 2, -60);
    ps.maxEmitBox = new Vector3(60, 30, 60);
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;

    switch (kind) {
      case 'lava':
        ps.color1 = new Color4(1.0, 0.6, 0.15, 0.9);
        ps.color2 = new Color4(1.0, 0.3, 0.1, 0.7);
        ps.colorDead = new Color4(0.6, 0.1, 0.0, 0);
        ps.minSize = 0.05;
        ps.maxSize = 0.18;
        ps.minLifeTime = 1.2;
        ps.maxLifeTime = 3.0;
        ps.emitRate = 80;
        ps.gravity = new Vector3(0, 0.6, 0);
        ps.direction1 = new Vector3(-0.2, 0.3, -0.2);
        ps.direction2 = new Vector3(0.2, 1.0, 0.2);
        ps.minEmitPower = 0.3;
        ps.maxEmitPower = 1.0;
        break;
      case 'ice':
        ps.color1 = new Color4(0.9, 0.95, 1.0, 0.85);
        ps.color2 = new Color4(0.8, 0.85, 1.0, 0.55);
        ps.colorDead = new Color4(0.6, 0.7, 0.9, 0);
        ps.minSize = 0.06;
        ps.maxSize = 0.2;
        ps.minLifeTime = 2.0;
        ps.maxLifeTime = 5.0;
        ps.emitRate = 120;
        ps.gravity = new Vector3(0.1, -0.8, 0.05);
        ps.direction1 = new Vector3(-0.4, -0.2, -0.4);
        ps.direction2 = new Vector3(0.4, -0.5, 0.4);
        ps.minEmitPower = 0.2;
        ps.maxEmitPower = 0.6;
        break;
      case 'toxic':
        ps.color1 = new Color4(0.55, 0.95, 0.35, 0.65);
        ps.color2 = new Color4(0.35, 0.7, 0.2, 0.5);
        ps.colorDead = new Color4(0.2, 0.45, 0.1, 0);
        ps.minSize = 0.1;
        ps.maxSize = 0.4;
        ps.minLifeTime = 3.0;
        ps.maxLifeTime = 6.5;
        ps.emitRate = 50;
        ps.gravity = new Vector3(0, 0.05, 0);
        ps.direction1 = new Vector3(-0.2, 0.1, -0.2);
        ps.direction2 = new Vector3(0.2, 0.3, 0.2);
        ps.minEmitPower = 0.15;
        ps.maxEmitPower = 0.45;
        break;
      case 'desert':
        ps.color1 = new Color4(0.95, 0.85, 0.55, 0.6);
        ps.color2 = new Color4(0.7, 0.5, 0.25, 0.45);
        ps.colorDead = new Color4(0.4, 0.25, 0.1, 0);
        ps.minSize = 0.08;
        ps.maxSize = 0.25;
        ps.minLifeTime = 1.5;
        ps.maxLifeTime = 3.5;
        ps.emitRate = 90;
        ps.gravity = new Vector3(0.3, 0.0, 0.2);
        ps.direction1 = new Vector3(0.4, -0.1, 0.4);
        ps.direction2 = new Vector3(1.2, 0.1, 1.2);
        ps.minEmitPower = 0.4;
        ps.maxEmitPower = 1.1;
        break;
      default:
        try { ps.dispose(); anchor.dispose(); } catch (_e) { /* noop */ }
        return;
    }
    ps.updateSpeed = 0.018;
    ps.start();
    this._biomeHaze = { ps, anchor, kind };
    this._continuous.push(ps);
  }

  /** Per-frame: follow the player position with biome haze + decay mining sparks
   *  that haven't been refreshed in a few frames. */
  tick() {
    if (this._disposed) return;
    this._tickDecays();
    if (this._biomeHaze && this.getPlayerPos) {
      try {
        const p = this.getPlayerPos();
        if (p) this._biomeHaze.anchor.position.set(p.x, p.y, p.z);
      } catch (_e) { /* noop */ }
    }
    // Auto-stop mining sparks if not refreshed recently (the mining loop calls
    // spawnMiningSparks every active frame; ~120ms without a refresh = done).
    if (this._mining) {
      const now = performance.now();
      if (this._mining._lastTickAt && now - this._mining._lastTickAt > 140) {
        try { this._mining.emitRate = 0; } catch (_e) { /* noop */ }
      } else if (this._mining.emitRate === 0 && this._mining._lastTickAt) {
        this._mining.emitRate = 80;
      }
    }
  }

  /** Decay expanding shockwave rings and flash lights spawned by explosions. */
  _tickDecays() {
    const now = performance.now();
    const dt = Math.min(0.05, (now - (this._lastNow || now)) / 1000);
    this._lastNow = now;
    if (this._shockwaves.length) {
      const keep = [];
      for (const sw of this._shockwaves) {
        sw.t += dt;
        const k = sw.t / sw.dur;
        if (k >= 1) { try { sw.mesh.dispose(); sw.mat.dispose(); } catch (_e) { /* noop */ } continue; }
        const s = 1 + k * sw.maxScale;
        sw.mesh.scaling.set(s, s, s);
        sw.mat.alpha = 0.9 * (1 - k);
        keep.push(sw);
      }
      this._shockwaves = keep;
    }
    if (this._flashes.length) {
      const keep = [];
      for (const fl of this._flashes) {
        fl.t += dt;
        const k = fl.t / fl.dur;
        if (k >= 1) { try { fl.light.dispose(); } catch (_e) { /* noop */ } continue; }
        fl.light.intensity = fl.base * (1 - k);
        keep.push(fl);
      }
      this._flashes = keep;
    }
  }


  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    for (const ps of this._systems) try { ps.dispose(); } catch (_e) { /* noop */ }
    for (const ps of this._continuous) try { ps.dispose(); } catch (_e) { /* noop */ }
    if (this._biomeHaze) {
      try { this._biomeHaze.anchor.dispose(); } catch (_e) { /* noop */ }
    }
    if (this._exhaust) {
      for (const a of this._exhaust.anchors) try { a.dispose(); } catch (_e) { /* noop */ }
    }
    for (const sw of this._shockwaves) { try { sw.mesh.dispose(); sw.mat.dispose(); } catch (_e) { /* noop */ } }
    for (const fl of this._flashes) { try { fl.light.dispose(); } catch (_e) { /* noop */ } }
    this._shockwaves = [];
    this._flashes = [];
    try { this._sparkTex?.dispose(); } catch (_e) { /* noop */ }
    this._systems = [];
    this._continuous = [];
    this._exhaust = null;
    this._biomeHaze = null;
    this._mining = null;
    this._sparkTex = null;
  }
}

// -------- Asteroid field (space mode) --------

/**
 * Build a static-ish asteroid field parented to a TransformNode (typically
 * spaceRoot). Returns the parent for caller convenience and exposes a `tick`
 * function for slow rotation. Asteroids drift on a slow, deterministic orbit
 * but are not collidable or mineable in 12a — pure ambience.
 */
export function buildAsteroidField(scene, parent, opts = {}) {
  const seed = (opts.seed | 0) || 0xa57e201d;
  const count = opts.count || 80;
  const minR = opts.innerRadius || 400;
  const maxR = opts.outerRadius || 5200;
  const fieldRoot = new TransformNode('asteroidField', scene);
  fieldRoot.parent = parent;

  function rng() {
    // tiny LCG so the field is deterministic per system
    let s = (seed ^ 0x9e3779b9) >>> 0;
    return () => {
      s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
      return s / 0xffffffff;
    };
  }
  const next = rng();

  const mat = new StandardMaterial('asteroidMat', scene);
  mat.diffuseColor = new Color3(0.32, 0.3, 0.28);
  mat.specularColor = new Color3(0.05, 0.05, 0.05);
  mat.emissiveColor = new Color3(0.05, 0.04, 0.03);

  const rocks = [];
  for (let i = 0; i < count; i++) {
    const r = minR + next() * (maxR - minR);
    const ang = next() * Math.PI * 2;
    const y = (next() - 0.5) * 200;
    const size = 4 + next() * 22;
    // Lower-poly rocks (icospheres look too uniform); use a stretched sphere.
    const m = MeshBuilder.CreateSphere(`ast_${i}`, { diameter: size, segments: 6 }, scene);
    m.position = new Vector3(Math.cos(ang) * r, y, Math.sin(ang) * r);
    m.scaling = new Vector3(
      0.7 + next() * 0.8,
      0.7 + next() * 0.8,
      0.7 + next() * 0.8,
    );
    m.rotation = new Vector3(next() * Math.PI * 2, next() * Math.PI * 2, next() * Math.PI * 2);
    m.material = mat;
    m.isPickable = false;
    m.parent = fieldRoot;
    rocks.push({
      mesh: m,
      radius: r,
      angle: ang,
      omega: (0.002 + next() * 0.006) * (next() > 0.5 ? 1 : -1),
      spin: new Vector3(
        (next() - 0.5) * 0.4,
        (next() - 0.5) * 0.4,
        (next() - 0.5) * 0.4,
      ),
      y,
    });
  }

  return {
    parent: fieldRoot,
    tick(dt) {
      for (const r of rocks) {
        r.angle += r.omega * dt;
        r.mesh.position.x = Math.cos(r.angle) * r.radius;
        r.mesh.position.z = Math.sin(r.angle) * r.radius;
        r.mesh.rotation.x += r.spin.x * dt;
        r.mesh.rotation.y += r.spin.y * dt;
        r.mesh.rotation.z += r.spin.z * dt;
      }
    },
    dispose() {
      try { fieldRoot.dispose(); } catch (_e) { /* noop */ }
    },
  };
}
